import {
    Component,
    OnInit,
    Input,
    OnChanges,
    SimpleChanges,
    Output,
    EventEmitter
} from '@angular/core';



@Component({
    selector: 'password-strength-meter',
    templateUrl: './password-strength-meter.component.html',
    styles: [`
        *,
        *::before,
        *::after {
          box-sizing: border-box;
        }

        .strength-meter {
          position: relative;
          height: 3px;
          background: #ddd;
          margin: 10px auto 10px;
          border-radius: 3px;
        }

        .strength-meter:before,
        .strength-meter:after {
          content: '';
          height: inherit;
          background: transparent;
          display: block;
          border-color: #fff;
          border-style: solid;
          border-width: 0 5px 0 5px;
          position: absolute;
          width: calc(((100% - 20px) / 5) + 10px);
          z-index: 10;
        }

        .strength-meter:before {
          left: calc((100% - 20px) / 5);
        }

        .strength-meter:after {
          right: calc((100% - 20px) / 5);
        }

        .strength-meter-fill {
          background: transparent;
          height: inherit;
          position: absolute;
          width: 0;
          border-radius: inherit;
          transition: width 0.5s ease-in-out, background 0.25s;
        }

        .strength-meter-fill[data-strength='0'] {
          background: darkred;
          width: 20%;
        }

        .strength-meter-fill[data-strength='1'] {
          background: orangered;
          width: 40%;
        }

        .strength-meter-fill[data-strength='2'] {
          background: orange;
          width: 60%;
        }

        .strength-meter-fill[data-strength='3'] {
          background: yellowgreen;
          width: 80%;
        }

        .strength-meter-fill[data-strength='4'] {
          background: green;
          width: 100%;
        }
    `],
    standalone: false
})
export class PasswordStrengthMeterComponent implements OnInit, OnChanges {
    @Input() password: string;

    @Input() colors: string[] = [];

    passwordStrength: number = null;
    idx: number = null;

    private prevPasswordStrength = null;
    repeatedCharacterCountArray: Array<KeyValuePair> = new Array(0);
    repeatedCharactersCount: number;

    private defaultColours = [
        'darkred',
        'orangered',
        'orange',
        'yellowgreen',
        'green'
    ];
    ngOnInit() { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.password) {
            this.calculatePasswordStrength();
        }
    }

    private countRepeatedCharacters(character: string, isNumber: boolean) {
        if (!isNumber) {
            character = character.toLowerCase();
        }
        var index = this.repeatedCharacterCountArray.findIndex(x => x.key === character);
        if (index !== -1) {
            this.repeatedCharacterCountArray[index].value += 1;
        }
        else {
            var keyValue = new KeyValuePair();
            keyValue.key = character;
            keyValue.value = 1;
            this.repeatedCharacterCountArray.push(keyValue);
        }
    }

    private calculatePasswordStrength() {

        let complexityScore: number = 0; // Initial score of 10 since all 5 requirements are already met (n * 2)
        let idx: number = 0;
        let upper_count = 0, consecutiveUpperCount = 0;
        let lower_count = 0, consecutiveLowerCount = 0;
        let number_count = 0, consecutiveNumberCount = 0;
        let specialCharacters: number = 0;
        let upperCountList = new Array(0);
        let lowerCountList = new Array(0);
        let numberCountList = new Array(0);
        this.repeatedCharacterCountArray = new Array(0);
        this.repeatedCharactersCount = 0;
        

        while (idx < this.password.length) {

            if (this.password[idx] >= 'A' && this.password[idx] <= 'Z') {
                upper_count = 0;
                while (idx < this.password.length && this.password[idx] >= 'A' && this.password[idx] <= 'Z') {
                    this.countRepeatedCharacters(this.password[idx], false);
                    upper_count += 1;
                    idx += 1;
                    
                }
                upperCountList.push(upper_count);
            }

            else if (this.password[idx] >= 'a' && this.password[idx] <= 'z') {
                lower_count = 0;
                while (idx < this.password.length && this.password[idx] >= 'a' && this.password[idx] <= 'z') {
                    this.countRepeatedCharacters(this.password[idx], false);
                    lower_count += 1;
                    idx += 1;
                    
                }
                lowerCountList.push(lower_count);
            }
            else if (this.password[idx] >= '0' && this.password[idx] <= '9') {
                number_count = 0;
                while (idx < this.password.length && this.password[idx] >= '0' && this.password[idx] <= '9') {
                    this.countRepeatedCharacters(this.password[idx], true);
                    number_count += 1;
                    idx += 1;
                    
                }
                numberCountList.push(number_count);
            }

            else {
                specialCharacters += 1;
                idx += 1;
            }
        }
        upper_count = 0;
        lower_count = 0;
        number_count = 0;

        console.log(this.repeatedCharacterCountArray);

        for (let i = 0; i < upperCountList.length; i++)
        {
            upper_count = upper_count + upperCountList[i];
            
            if (upperCountList[i] > 1) {
                consecutiveUpperCount += upperCountList[i] - 1;
            }
        }

        for (let i = 0; i < lowerCountList.length; i++)
        {
            lower_count += lowerCountList[i];
            if (lowerCountList[i] > 1) {
                consecutiveLowerCount += lowerCountList[i] - 1;
            }
        }

        for (let i = 0; i < numberCountList.length; i++)
        {
            number_count += numberCountList[i];
            if (numberCountList[i] > 1) {
                consecutiveNumberCount += numberCountList[i] - 1;
            }
        }

        // number of characters
        complexityScore += (this.password.length * 4);


        // Uppercase letters
        complexityScore += ((this.password.length - upper_count) * 2);

        // lowercase letters
        complexityScore += ((this.password.length - lower_count) * 2);

        // Numbers
        complexityScore += (number_count * 4);

        // Symbols
        complexityScore += (specialCharacters * 6);

        // Deductions

        // Only numbers
        let onlyNumber = /^\d+$/.test(this.password);
        if (onlyNumber) {
            complexityScore -= this.password.length;
        }

        // Only letters
        let onlyLetters = /^[a-zA-Z]+$/.test(this.password);
        if (onlyLetters) {
            complexityScore -= this.password.length;
        }
        // Repeated Characters Count
        for (let i = 0; i < this.repeatedCharacterCountArray.length; i++) {
            if (this.repeatedCharacterCountArray[i].value > 1) {
                this.repeatedCharactersCount += this.repeatedCharacterCountArray[i].value;
            }
        }
        complexityScore -= Math.pow(this.repeatedCharactersCount, 2);
        
        // Consecutive Uppercase letters
        complexityScore -= (consecutiveUpperCount * 2);

        // Consecutive Lowercase letters
        complexityScore -= (consecutiveLowerCount * 2);

        // Consecutive numeric letters
        complexityScore -= (consecutiveNumberCount * 2);

        // character sequences
        var alphabetsCount =  this.sequencesCount(this.password);
        complexityScore -= (alphabetsCount.lowerCount + alphabetsCount.numberCount + alphabetsCount.specialCharacterCount) * 3;
        this.passwordStrength = complexityScore;
    }

    getMeterFillColor(strength) {
        this.idx = -1;
        //if (!strength || strength < 0 || strength > 5) {
        //    return this.colors[0] ? this.colors[0] : this.defaultColours[0];
        //}
        if (strength > 90) {
            this.idx = 4;
        } else if (strength > 70) {
            this.idx = 3;
        } else if (strength >= 40) {
            this.idx = 2;
        } else if (strength >= 20) {
            this.idx = 1;
        }
        else if (strength >= 10) {
            this.idx = 0;
        }

        return this.defaultColours[this.idx];
    }

    sequencesCount(passwordSeq: string): AlphabetCount {

        let alphabetCounts = new AlphabetCount();
        passwordSeq = passwordSeq.toLowerCase() + "!";
        let lowerStartIndex = -1, lowerEndIndex = -1;
        let numberStartIndex = -1, numberEndIndex = -1;
        let specialCharacterStartIndex = -1, specialCharacterEndIndex = -1;
        let lowerSequenceCount = new Array(0);
        let numberSequenceCount = new Array(0);
        let specialCharacterSequenceCount = new Array(0);

        let charArray = Array.from(passwordSeq);
        for (let i = 1; i < passwordSeq.length; i++)
        {

            let a = passwordSeq[i];
            let b = passwordSeq[i - 1];
            let charCodeDiffrence: number = Math.abs(passwordSeq.charCodeAt(i) - passwordSeq.charCodeAt(i - 1));
            

            if (a.match(/[a-z]/i) && b.match(/[a-z]/i) && a === passwordSeq[i].toLowerCase() && b === passwordSeq[i - 1].toLowerCase() && charCodeDiffrence == 1) {
                if (lowerStartIndex == -1) {
                    lowerStartIndex = i - 1;

                }
                lowerEndIndex = i;
            }
            else if (a >= '0' && a <= '9' && b >= '0' && b <= '9' && charCodeDiffrence == 1) {
                if (numberStartIndex == -1) {
                    numberStartIndex = i - 1;

                }
                numberEndIndex = i;
            }
            else if (!a.match(/^[^a-zA-Z0-9]+$/) && !b.match(/^[^a-zA-Z0-9]+$/) && charCodeDiffrence == 1) {
                if (specialCharacterStartIndex == -1) {
                    specialCharacterStartIndex = i - 1;

                }
                specialCharacterEndIndex = i;
            }
            else {
                if (lowerStartIndex != -1) {
                    lowerSequenceCount.push(lowerEndIndex - lowerStartIndex);
                }
                if (numberStartIndex != -1) {
                    numberSequenceCount.push(numberEndIndex - numberStartIndex);
                }
                if (specialCharacterEndIndex != -1) {
                    specialCharacterSequenceCount.push(specialCharacterEndIndex - specialCharacterStartIndex);
                }
                lowerStartIndex = lowerEndIndex = numberStartIndex = numberEndIndex = specialCharacterStartIndex = specialCharacterEndIndex = -1;
            }

        }

        alphabetCounts.lowerCount = lowerSequenceCount?.length > 0 ? Math.max.apply(null, lowerSequenceCount) - 1 : 0;
        alphabetCounts.numberCount = numberSequenceCount?.length > 0 ? Math.max.apply(null, numberSequenceCount) - 1 : 0;
        alphabetCounts.specialCharacterCount = specialCharacterSequenceCount?.length > 0 ? Math.max.apply(null, specialCharacterSequenceCount) - 1 : 0;

        return alphabetCounts;
    }
}

class AlphabetCount {
    public lowerCount: number;
    public numberCount: number;
    public specialCharacterCount: number;
}

class KeyValuePair {
    public key: string;
    public value: number
}